import React, { useState } from 'react';
import logo from "@images/sermyble_Logo.png";
import "@styles/loginpageui.css";
import { InputText } from "primereact/inputtext";
import { Link, useNavigate } from "react-router-dom";
import { ForgetPassword, ResetPassword } from '@services/api/login/loginService';

const ForgotPasswordPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleRequestOtp = async () => {
    setLoading(true);
    try {
      const data = { "userName": username };
      const response = await ForgetPassword(data);
      setMessage(response.message);
      setStep(2);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setMessage("User not found!!");
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    try {
      const data = { "username": username, "otp": otp, "newPassword": newPassword };
      const response = await ResetPassword(data);
      setMessage(response.message);
      setLoading(false);
      navigate("/login");
    } catch (error) {
      setMessage("Invalid OTP or OTP expired");
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* ═══ LEFT — Brand Panel ═══ */}
      <div className="login-brand-panel">
        <div className="brand-bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="brand-content">
          <div className="brand-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <h1 className="brand-title">Password Reset</h1>
          <p className="brand-subtitle">Secure Recovery</p>
          <div className="brand-features">
            <div className="brand-feature">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span>OTP sent to your registered email</span>
            </div>
            <div className="brand-feature">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Secure password reset process</span>
            </div>
            <div className="brand-feature">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Back to login after reset</span>
            </div>
          </div>
        </div>
        <p className="brand-footer">© 2026 ASL Information Services</p>
      </div>

      {/* ═══ RIGHT — Form Panel ═══ */}
      <div className="login-form-panel">
        <div className="login-form-container">
          <div className="login-logo-wrap">
            <img src={logo} className="login-logo" alt="Logo" />
          </div>

          {step === 1 ? (
            <>
              <h2 className="login-title">Forgot Password</h2>
              <p className="login-desc">Enter your username and we'll send you an OTP to reset your password</p>
              <form className="login-form" onSubmit={(e) => { e.preventDefault(); handleRequestOtp(); }}>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    <InputText
                      name="username"
                      placeholder="Enter your username"
                      className="login-input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>
                </div>
                {message && <div className="login-error-banner">{message}</div>}
                <button type="submit" className="login-submit-btn" disabled={isLoading}>
                  {isLoading ? (
                    <span className="btn-loading">
                      <svg className="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                      Sending OTP...
                    </span>
                  ) : "Send OTP"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="login-title">Reset Password</h2>
              <p className="login-desc">Enter the OTP sent to your email and your new password</p>
              <form className="login-form" onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }}>
                <div className="form-group">
                  <label className="form-label">OTP</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <InputText
                      type="text"
                      placeholder="Enter OTP"
                      className="login-input"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    <InputText
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      className="login-input"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>
                {message && <div className="login-error-banner">{message}</div>}
                <button type="submit" className="login-submit-btn" disabled={isLoading}>
                  {isLoading ? (
                    <span className="btn-loading">
                      <svg className="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                      Resetting...
                    </span>
                  ) : "Reset Password"}
                </button>
              </form>
            </>
          )}

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Link to="/login" className="forgot-link">← Back to Login</Link>
          </div>

          <p className="login-footer-text">
            Secure reset powered by <strong>Sermyble</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
